import { useEffect } from "react";
import type { Article } from "@core/features/support";
import { useFetchArticle } from "@core/features/support";
import { videoEventBus } from "@core/features/video";
import { MdxArticleLinkProvider } from "@themes/pollinator/ui/MdxRenderer";
import { useVideoMaterialsNavigation } from "@platform/store";
import { VideoMeetingMaterials } from "./VideoMeetingMaterials";

type Props = {
  article: Article | null;
  scrollContainer?: HTMLElement | null;
};

export function VideoMeetingMaterialsContainer({
  article,
  scrollContainer,
}: Props) {
  const { stack, push, pop, clear } = useVideoMaterialsNavigation();
  const topSlug = stack.length > 0 ? stack[stack.length - 1] : null;

  const {
    data: navigatedArticle,
    isLoading: isFetchingNavigated,
    isError: didFetchFail,
  } = useFetchArticle(topSlug, !!topSlug);

  const displayedArticle: Article | null = topSlug
    ? (navigatedArticle ?? null)
    : article;

  const displayedSlug =
    topSlug ?? article?.slug ?? article?.id ?? null;

  // Track each viewed article (original + navigated). Fires whenever the
  // displayed slug changes, so deep-links and stack pops both produce events.
  useEffect(() => {
    if (!displayedSlug) return;
    videoEventBus.emitEvent("materials:article:viewed", {
      articleId: displayedSlug,
    });
  }, [displayedSlug]);

  if (!article && stack.length === 0) return null;

  const isLoading = !!topSlug && isFetchingNavigated && !navigatedArticle;
  const loadError =
    !!topSlug && !isFetchingNavigated && (didFetchFail || !navigatedArticle);

  return (
    <MdxArticleLinkProvider onArticleSelect={push}>
      <VideoMeetingMaterials
        article={displayedArticle}
        scrollContainer={scrollContainer}
        canGoBack={stack.length > 0}
        canResetToOriginal={stack.length > 1}
        isLoading={isLoading}
        loadError={loadError}
        onBack={pop}
        onResetToOriginal={clear}
      />
    </MdxArticleLinkProvider>
  );
}

export default VideoMeetingMaterialsContainer;
